import { createContext, useContext, useState, ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface InfoModalProviderProps {
  children: JSX.Element;
}

interface InfoModalContextInterface {
  showInfo: (infoMessage: string | ReactNode, buttonText?: string) => Promise<void>;
}

const InfoModalContext = createContext<InfoModalContextInterface>({
  showInfo: async () => {},
});

export const InfoModalProvider = ({ children }: InfoModalProviderProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string | ReactNode>("");
  const [buttonLabel, setButtonLabel] = useState<string>("Ok");
  const [resolvePromise, setResolvePromise] = useState<(() => void) | null>(null);
  const { t } = useTranslation();

  const showInfo = (infoMessage: string | ReactNode, buttonText?: string): Promise<void> => {
    return new Promise<void>((resolve) => {
      setMessage(infoMessage);
      setButtonLabel((buttonText as string) || (t("Ok") as string));
      setOpen(true);
      setResolvePromise(() => resolve);
    });
  };

  const handleClose = () => {
    setOpen(false);
    if (resolvePromise) {
      resolvePromise();
      setResolvePromise(null);
    }
  };

  return (
      <InfoModalContext.Provider value={{ showInfo }}>
        {open && (
            <div className="info-modal modal" tabIndex={-1} role="dialog">
              <div className="modal__dialog">
                <div className="modal__body">
                  {typeof message === "string" ? (
                      <div className="pt-4 text-center">{message}</div>
                  ) : (
                      message
                  )}
                </div>
                <div className="modal__footer">
                  <div className="flex justify-center">
                    <button className="button" onClick={handleClose}>
                      {buttonLabel}
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}
        {children}
      </InfoModalContext.Provider>
  );
};

export default InfoModalProvider;

export function useInfoModal() {
  return useContext(InfoModalContext);
}
